// Global Instructions Rule Applied!
//
// Configuration → Interaction Tracking → Meeting watches (admin only).
//
// Which recurring Teams meetings Reservoir polls for transcripts (the Deal
// Pipeline meeting agent's ingest, design §8.E). The admin pastes the
// meeting's JOIN LINK from the Teams invite; "Resolve" proves the READER app
// can see the meeting via Graph and caches its onlineMeeting id. Recording/
// transcription itself stays a manual Teams toggle in v1 (owner 2026-08-27) —
// the organizer enables "Record and transcribe automatically" in Meeting
// options.
//
// Chrome copied from InteractionTrackingPanel (this section's host): a
// FormSection + AntD Table, refusals inline (static antd message renders
// nowhere under React 19).
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Input, Modal, Popconfirm, Switch, Table, Tag, Tooltip } from 'antd'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { MeetingWatchRow } from '../../../state_manager/data_models/meeting_watch_model.ts'
import {
  MeetingWatchSavePayload,
  deleteMeetingWatch,
  fetchMeetingWatches,
  resolveMeetingWatch,
  saveMeetingWatch
} from '../services/meetingWatchService.ts'

const fmtDate = (v?: string | null): string =>
  v ? new Date(v).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'

const EMPTY_DRAFT: MeetingWatchSavePayload = {
  label: '',
  organizer_email: '',
  organizer_user_id: '',
  join_web_url: '',
  subject_match: '',
  enabled: true
}

export default function MeetingWatchesSection(): React.ReactElement {
  const token = (): string | null => getAccessToken(store.getState().auth)

  const [rows, setRows] = useState<MeetingWatchRow[]>([])
  const [loading, setLoading] = useState(true)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [draft, setDraft] = useState<MeetingWatchSavePayload | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchMeetingWatches(token()))
    } catch (error) {
      console.error('[meeting-watches] load failed:', error)
      setRefusal(error instanceof Error ? error.message : 'Failed to load meeting watches')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const run = async (action: () => Promise<unknown>, failureLabel: string): Promise<boolean> => {
    setRefusal(null)
    try {
      await action()
      await load()
      return true
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : failureLabel)
      return false
    }
  }

  return (
    <>
      <FormSection
        title='Meeting watches'
        titleColor='purple'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => setDraft({ ...EMPTY_DRAFT })}
            >
              Add watch
            </Button>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </span>
        }
      >
        {refusal && (
          <Alert
            type='error'
            showIcon
            closable
            style={{ marginBottom: 12 }}
            message={refusal}
            onClose={() => setRefusal(null)}
          />
        )}
        <Table
          size='small'
          rowKey='agent_meeting_watches__id'
          pagination={false}
          dataSource={rows}
          locale={{ emptyText: 'No meeting watches yet — add the Deal Pipeline meeting’s join link.' }}
          columns={[
            {
              title: 'Meeting',
              dataIndex: 'agent_meeting_watches__label',
              render: (value: string, row: MeetingWatchRow) => (
                <button
                  type='button'
                  className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                  onClick={() =>
                    setDraft({
                      id: row.agent_meeting_watches__id,
                      label: row.agent_meeting_watches__label,
                      organizer_email: row.agent_meeting_watches__organizer_email,
                      organizer_user_id: row.agent_meeting_watches__organizer_user_id || '',
                      join_web_url: row.agent_meeting_watches__join_web_url,
                      subject_match: row.agent_meeting_watches__subject_match || '',
                      enabled: row.agent_meeting_watches__enabled
                    })
                  }
                >
                  {value}
                </button>
              )
            },
            { title: 'Organizer', dataIndex: 'agent_meeting_watches__organizer_email' },
            {
              title: 'Resolved',
              dataIndex: 'agent_meeting_watches__online_meeting_id',
              render: (value: string | null, row: MeetingWatchRow) =>
                value ? (
                  <Tag color='green'>Resolved</Tag>
                ) : row.agent_meeting_watches__resolve_error ? (
                  <Tooltip title={row.agent_meeting_watches__resolve_error}>
                    <Tag color='red'>Error</Tag>
                  </Tooltip>
                ) : (
                  <Tag>Pending</Tag>
                )
            },
            {
              title: 'Enabled',
              dataIndex: 'agent_meeting_watches__enabled',
              width: 80,
              render: (value: boolean, row: MeetingWatchRow) => (
                <Switch
                  size='small'
                  checked={value}
                  loading={busyId === row.agent_meeting_watches__id}
                  onChange={async (next) => {
                    setBusyId(row.agent_meeting_watches__id)
                    await run(
                      () =>
                        saveMeetingWatch(token(), {
                          id: row.agent_meeting_watches__id,
                          label: row.agent_meeting_watches__label,
                          organizer_email: row.agent_meeting_watches__organizer_email,
                          organizer_user_id: row.agent_meeting_watches__organizer_user_id,
                          join_web_url: row.agent_meeting_watches__join_web_url,
                          subject_match: row.agent_meeting_watches__subject_match,
                          enabled: next
                        }),
                      'Failed to update the watch'
                    )
                    setBusyId(null)
                  }}
                />
              )
            },
            {
              title: 'Last transcript',
              dataIndex: 'agent_meeting_watches__last_transcript_at',
              width: 120,
              render: (value: string | null) => fmtDate(value)
            },
            {
              title: 'Transcripts',
              dataIndex: 'agent_meeting_watches__transcript_count',
              width: 90,
              align: 'right' as const
            },
            {
              title: '',
              key: 'actions',
              width: 150,
              render: (_: unknown, row: MeetingWatchRow) => (
                <span className='flex items-center gap-1'>
                  <Button
                    size='small'
                    loading={busyId === `resolve-${row.agent_meeting_watches__id}`}
                    onClick={async () => {
                      setBusyId(`resolve-${row.agent_meeting_watches__id}`)
                      await run(
                        () => resolveMeetingWatch(token(), row.agent_meeting_watches__id),
                        'Failed to resolve the watch'
                      )
                      setBusyId(null)
                    }}
                  >
                    Resolve
                  </Button>
                  <Popconfirm
                    title='Delete this meeting watch?'
                    okText='Delete'
                    onConfirm={() =>
                      run(
                        () => deleteMeetingWatch(token(), row.agent_meeting_watches__id),
                        'Failed to delete the watch'
                      )
                    }
                  >
                    <Button size='small' danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </span>
              )
            }
          ]}
        />
        <span className='text-xs text-gray-500'>
          Paste the recurring meeting&apos;s Join link from the Teams invite plus the
          organizer&apos;s Object ID (Entra → Users → the person → Object ID — Graph&apos;s meeting
          endpoints reject an email address), then Resolve. Transcripts
          are polled every 15 minutes after each occurrence; recording/transcription itself is the
          organizer&apos;s &quot;Record and transcribe automatically&quot; toggle in Teams Meeting options.
        </span>
      </FormSection>

      <Modal
        title={draft?.id ? 'Edit meeting watch' : 'Add a meeting watch'}
        open={draft !== null}
        onCancel={() => setDraft(null)}
        okText={draft?.id ? 'Save' : 'Add'}
        okButtonProps={{
          disabled:
            !draft?.label?.trim() ||
            !draft?.organizer_email?.includes('@') ||
            !draft?.join_web_url?.trim()
        }}
        onOk={async () => {
          if (!draft) return
          const ok = await run(
            () =>
              saveMeetingWatch(token(), {
                ...draft,
                label: draft.label.trim(),
                organizer_email: draft.organizer_email.trim(),
                organizer_user_id: draft.organizer_user_id?.trim() || null,
                join_web_url: draft.join_web_url.trim(),
                subject_match: draft.subject_match?.trim() || null
              }),
            'Failed to save the watch'
          )
          if (ok) setDraft(null)
        }}
        destroyOnClose
      >
        <div className='flex flex-col gap-2 pt-2'>
          <Input
            placeholder='Label (e.g. Weekly Deal Pipeline)'
            value={draft?.label}
            onChange={(e) => setDraft((d) => (d ? { ...d, label: e.target.value } : d))}
          />
          <Input
            placeholder='Organizer email'
            value={draft?.organizer_email}
            onChange={(e) => setDraft((d) => (d ? { ...d, organizer_email: e.target.value } : d))}
          />
          <Input
            placeholder='Organizer Object ID (Entra → Users → Object ID)'
            value={draft?.organizer_user_id || ''}
            onChange={(e) => setDraft((d) => (d ? { ...d, organizer_user_id: e.target.value } : d))}
          />
          <Input
            placeholder='Teams join link (from the meeting invite)'
            value={draft?.join_web_url}
            onChange={(e) => setDraft((d) => (d ? { ...d, join_web_url: e.target.value } : d))}
          />
          <Input
            placeholder='Subject match, e.g. %deal pipeline% (optional)'
            value={draft?.subject_match || ''}
            onChange={(e) => setDraft((d) => (d ? { ...d, subject_match: e.target.value } : d))}
          />
        </div>
      </Modal>
    </>
  )
}
